import React from 'react'
import { hashHistory } from 'react-router'
import {
  CircularProgress,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import TableInc from '../../../../../shared-ui/TableInc'
import { formatKes, StatusPill } from '../PaymentsList/PaymentsList'
import { groupLabel, userLabel } from '../../lib/labels'

const headerCard = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '16px 20px'
}

const detailCard = {
  background: '#FFFFFF',
  border: '1px solid #D9D9D9',
  boxShadow: '0px 8px 24px rgba(149, 157, 165, 0.2)',
  borderRadius: '4px',
  padding: '20px',
  marginTop: '16px',
  marginBottom: '20px'
}

const labelStyle = {
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.005em',
  color: '#A9A9A9',
  margin: 0
}

const valueStyle = {
  fontFamily: 'Lato',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '20px',
  letterSpacing: '0.001em',
  color: '#000000',
  margin: 0
}

const sectionTitleStyle = {
  fontFamily: 'Lato',
  fontWeight: 600,
  fontSize: '14px',
  color: '#000000',
  margin: 0
}

const rowStyle = {
  background: '#F8FAFC',
  borderRadius: '8px'
}

// The amount allocated to one policy within a group payment. Staging serves it
// as `payment_amount_in_cents`; the other two are accepted because the
// per-policy payload has no published schema and older rows may differ.
export function firstAmount(row) {
  if (!row) {
    return undefined
  }
  const candidates = [
    row.payment_amount_in_cents,
    row.premium_amount_in_cents,
    row.amount_in_cents
  ]
  for (let i = 0; i < candidates.length; i++) {
    if (typeof candidates[i] === 'number') {
      return candidates[i]
    }
  }
  return undefined
}

function safeText(value) {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return value
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : '—'
}

function Field(props) {
  return (
    <Grid item xs={12} sm={props.sm || 4} md={props.md || 4}>
      <p style={labelStyle}>{props.label}</p>
      <h6 style={valueStyle}>{safeText(props.value)}</h6>
    </Grid>
  )
}

function CardHeader(props) {
  return (
    <div>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1.5}>
          <div
            style={{
              width: '30px',
              height: '30px',
              background: '#032A37',
              borderRadius: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PaymentsOutlinedIcon
              style={{ color: '#ffffff', fontSize: '16px' }}
            />
          </div>
          <h6 style={sectionTitleStyle}>{props.title}</h6>
        </Box>
        {props.status && <StatusPill status={props.status} />}
      </Box>
      <div
        style={{
          borderBottom: '1px solid #D9D9D9',
          width: '100%',
          margin: '16px 0'
        }}
      />
    </div>
  )
}

class PaymentDetail extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      decisionDialogOpen: false,
      decisionStatus: 'processing',
      notes: ''
    }
    this.openDecisionDialog = this.openDecisionDialog.bind(this)
    this.closeDecisionDialog = this.closeDecisionDialog.bind(this)
    this.handleConfirmDecision = this.handleConfirmDecision.bind(this)
    this.goBack = this.goBack.bind(this)
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.update_status_success && this.props.update_status_success) {
      this.props.resetUpdateStatus()
      hashHistory.push('admin/group-payments-dashboard')
    }
  }

  goBack() {
    hashHistory.push('admin/group-payments-dashboard')
  }

  openDecisionDialog(decisionStatus) {
    this.setState({
      decisionDialogOpen: true,
      decisionStatus,
      notes: ''
    })
  }

  closeDecisionDialog() {
    this.setState({ decisionDialogOpen: false })
  }

  handleConfirmDecision() {
    const payment = this.props.payment
    if (!payment) return
    this.setState({ decisionDialogOpen: false })
    this.props.updateStatus({
      group_guid: payment.group_guid || this.props.groupGuid,
      payment_guid: payment.guid || this.props.paymentGuid,
      status: this.state.decisionStatus,
      notes: this.state.notes
    })
  }

  renderBreakdown(payment) {
    const policyPayments = payment.policy_payments || []
    const columns = [
      {
        title: 'Policy',
        field: 'policy_number',
        render(rowData) {
          return safeText(rowData.policy_number || rowData.policy_guid)
        }
      },
      {
        // The per-policy rows carry the allocated amount as
        // `payment_amount_in_cents` — confirmed against staging, where every
        // row has it and none has `premium_amount_in_cents`. Reading the
        // latter first is why this column rendered as a dash for every policy.
        // The other two names are kept as fallbacks rather than removed, since
        // the payload is not documented anywhere.
        title: 'Premium',
        field: 'payment_amount_in_cents',
        render(rowData) {
          return formatKes(firstAmount(rowData))
        }
      },
      {
        title: 'Status',
        field: 'status',
        render(rowData) {
          return <StatusPill status={rowData.status} />
        }
      },
      {
        title: 'Failure Reason',
        field: 'failure_reason',
        render(rowData) {
          return safeText(rowData.failure_reason)
        }
      }
    ]
    return (
      <div style={detailCard}>
        <CardHeader title="Per-policy Breakdown" />
        <TableInc
          columns={columns}
          data={policyPayments}
          options={{
            search: false,
            paging: false,
            toolbar: false
          }}
          localization={{
            body: {
              emptyDataSourceMessage: 'No policy payments found'
            }
          }}
        />
      </div>
    )
  }

  renderProof(payment) {
    const documents = payment.proof_of_payment_documents || []
    return (
      <div style={detailCard}>
        <CardHeader title="Proof of Payment" />
        {documents.length === 0 ? (
          <p style={labelStyle}>No proof of payment uploaded.</p>
        ) : (
          documents.map(function(doc, i) {
            return (
              <Box
                key={i}
                mb={1}
                p={1.5}
                style={rowStyle}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <h6 style={valueStyle}>
                  {safeText(doc.name || `Document ${i + 1}`)}
                </h6>
                {doc.url ? (
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '13px',
                      color: '#032A37',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      textDecoration: 'none',
                      fontWeight: 600
                    }}
                  >
                    View <OpenInNewIcon style={{ fontSize: '14px' }} />
                  </a>
                ) : (
                  <span style={labelStyle}>Not available</span>
                )}
              </Box>
            )
          })
        )}
      </div>
    )
  }

  render() {
    const payment = this.props.payment

    if (this.props.loader) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          p={6}
        >
          <CircularProgress style={{ color: '#032A37' }} />
          <span style={labelStyle}>Loading payment details…</span>
        </Box>
      )
    }

    if (this.props.error) {
      return (
        <div>
          <Alert severity="error" style={{ borderRadius: '8px' }}>
            {safeText(this.props.error)}
          </Alert>
          <Box mt={2}>
            <ButtonInc
              btnType="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={this.goBack}
            >
              Back to Payments
            </ButtonInc>
          </Box>
        </div>
      )
    }

    if (!payment) return null

    const canDecide = payment.status === 'pending_approval'

    return (
      <div>
        <Box mb={2}>
          <ButtonInc
            btnType="secondary"
            startIcon={<ArrowBackIcon style={{ fontSize: '16px' }} />}
            onClick={this.goBack}
          >
            Back to Payments
          </ButtonInc>
        </Box>

        {/* Header card */}
        <section style={headerCard}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  background: '#032A37',
                  borderRadius: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <PaymentsOutlinedIcon
                  style={{ color: '#ffffff', fontSize: '18px' }}
                />
              </div>
              <div>
                <h1
                  style={{
                    fontFamily: 'Lato',
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#000000',
                    margin: 0
                  }}
                >
                  Group Payment
                </h1>
                <span style={labelStyle}>
                  {safeText(payment.payment_reference)} ·{' '}
                  {groupLabel(this.props.groupLabels, payment.group_guid)}
                </span>
              </div>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <StatusPill status={payment.status} />
              {canDecide && (
                <ButtonInc
                  btnType="secondary"
                  onClick={function() {
                    this.openDecisionDialog('rejected')
                  }.bind(this)}
                  disabled={this.props.update_status_loader}
                >
                  Reject
                </ButtonInc>
              )}
              {canDecide && (
                <ButtonInc
                  variant="contained"
                  onClick={function() {
                    this.openDecisionDialog('processing')
                  }.bind(this)}
                  loading={this.props.update_status_loader}
                  disabled={this.props.update_status_loader}
                >
                  Approve
                </ButtonInc>
              )}
            </Box>
          </Box>
        </section>

        {this.props.update_status_error && (
          <Alert
            severity="error"
            style={{ marginTop: '16px', borderRadius: '8px' }}
          >
            {safeText(this.props.update_status_error)}
          </Alert>
        )}

        {/* Summary */}
        <div style={detailCard}>
          <CardHeader title="Payment Summary" status={payment.status} />
          <Grid container spacing={2}>
            <Field
              label="Payment Reference"
              value={payment.payment_reference}
            />
            <Field
              label="Group"
              value={groupLabel(this.props.groupLabels, payment.group_guid)}
            />
            <Field
              label="Amount Submitted"
              value={formatKes(payment.payment_amount_in_cents)}
            />
            <Field
              label="Total Premium"
              value={formatKes(payment.total_premium_amount_in_cents)}
            />
            <Field
              label="Maker"
              value={userLabel(this.props.userLabels, payment.created_by)}
            />
            <Field
              label="Checker"
              value={userLabel(this.props.userLabels, payment.approved_by)}
            />
            <Field label="Created At" value={formatDate(payment.created_at)} />
            <Field label="Updated At" value={formatDate(payment.updated_at)} />
          </Grid>
        </div>

        {this.renderBreakdown(payment)}
        {this.renderProof(payment)}

        {/* Decision dialog */}
        <Dialog
          open={this.state.decisionDialogOpen}
          onClose={this.closeDecisionDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ style: { borderRadius: '8px', padding: '8px' } }}
        >
          <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 600 }}>
            {this.state.decisionStatus === 'processing'
              ? 'Approve Payment'
              : 'Reject Payment'}
          </DialogTitle>
          <DialogContent>
            <Box mt={1} display="flex" flexDirection="column" gap={2}>
              <span style={{ fontFamily: 'Open Sans', fontSize: '13px' }}>
                {this.state.decisionStatus === 'processing' ? (
                  <span>
                    Are you sure you want to approve payment{' '}
                    <strong>{safeText(payment.payment_reference)}</strong>? This
                    will dispatch it for processing.
                  </span>
                ) : (
                  <span>
                    Are you sure you want to reject payment{' '}
                    <strong>{safeText(payment.payment_reference)}</strong>?
                  </span>
                )}
              </span>
              <TextField
                label="Notes (optional)"
                value={this.state.notes}
                onChange={function(e) {
                  this.setState({ notes: e.target.value })
                }.bind(this)}
                multiline
                rows={3}
                fullWidth
                size="small"
              />
            </Box>
          </DialogContent>
          <DialogActions style={{ padding: '16px' }}>
            <ButtonInc
              btnType="secondary"
              onClick={this.closeDecisionDialog}
              disabled={this.props.update_status_loader}
            >
              Cancel
            </ButtonInc>
            <ButtonInc
              variant="contained"
              onClick={this.handleConfirmDecision}
              loading={this.props.update_status_loader}
              disabled={this.props.update_status_loader}
            >
              {this.state.decisionStatus === 'processing'
                ? 'Confirm Approve'
                : 'Confirm Reject'}
            </ButtonInc>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

export default PaymentDetail
